import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import * as XLSX from 'xlsx';
import { LocationItem, LocationService } from '../core/services/location.service';
import {
  MealOrderedReportCell,
  MealOrderedReportResponse,
  MealOrderedReportService,
} from '../core/services/meal-ordered-report.service';
import { getProgramId } from '../shared/utils/storage-utils';
import {
  buildReportDownloadSuffix,
  formatReportWeekLabel,
  formatReservedReceivedCell,
  getReportWeekBounds,
  resolveReportDateRange,
  shiftReportWeekRange,
} from '../shared/utils/report-date-range';

type ReportRow = {
  locationId: string;
  locationName: string;
  values: Record<string, string>;
};

export type MealGridReportMode = 'ordered' | 'delivered';

@Component({
  selector: 'meal-report-management-component',
  standalone: true,
  templateUrl: './meal-report-management.component.html',
  styleUrls: ['./meal-report-management.component.css'],
  imports: [CommonModule, FormsModule, RouterModule],
})
export class MealReportManagementComponent implements OnInit {
  private readonly seniorCenterProgramId = 1000;

  isInitialLoading = true;
  isRefreshing = false;
  errorMessage: string | null = null;

  reportMode: MealGridReportMode = 'ordered';
  reportTitle = 'Meal Report';

  centers: LocationItem[] = [];
  filterCenterId = '';
  /** Figma: This Week navigator (prev / next week). */
  private readonly timeframe = 'weekly' as const;
  private lastFromDate = '';
  private lastToDate = '';

  customFromDate = '';
  customToDate = '';
  weekLabel = 'This Week';

  dateKeys: string[] = [];
  dateLabels: string[] = [];
  rows: ReportRow[] = [];

  private cells: MealOrderedReportCell[] = [];

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly locationService: LocationService,
    private readonly reportService: MealOrderedReportService,
  ) {}

  ngOnInit(): void {
    const token = sessionStorage.getItem('token');
    if (!token) {
      void this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    const mode = (this.route.snapshot.data['mealGridMode'] as MealGridReportMode) || 'ordered';
    this.applyReportMode(mode === 'delivered' ? 'delivered' : 'ordered');
    this.applyWeekBounds(getReportWeekBounds());

    this.locationService.getActiveLocations().subscribe({
      next: (response) => {
        this.centers = response.locations ?? [];
        this.loadReport();
      },
      error: () => {
        this.centers = [];
        this.loadReport();
      },
    });
  }

  goBack(): void {
    void this.router.navigate(['/manage-services/senior-center/reports']);
  }

  setReportMode(mode: MealGridReportMode): void {
    if (this.reportMode === mode) {
      return;
    }
    this.applyReportMode(mode);
    if (this.lastFromDate && this.lastToDate) {
      this.buildMatrix(this.lastFromDate, this.lastToDate);
    }
  }

  onFiltersChanged(): void {
    this.loadReport({ silent: true });
  }

  shiftWeek(weekOffset: number): void {
    if (!this.customFromDate) {
      this.applyWeekBounds(getReportWeekBounds());
    } else {
      this.applyWeekBounds(shiftReportWeekRange(this.customFromDate, weekOffset));
    }
    this.loadReport({ silent: true });
  }

  resetToThisWeek(): void {
    this.applyWeekBounds(getReportWeekBounds());
    this.loadReport({ silent: true });
  }

  onDownload(): void {
    if (!this.rows.length || !this.dateKeys.length) {
      return;
    }

    const sheetRows = this.rows.map((row) => {
      const record: Record<string, string> = { Center: row.locationName };
      this.dateKeys.forEach((key, index) => {
        record[this.dateLabels[index]] = row.values[key] ?? this.formatCellValue(0, 0);
      });
      return record;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, this.reportTitle);
    const suffix = buildReportDownloadSuffix({
      timeframe: this.timeframe,
      selectedYear: '',
      selectedMonthNumber: '',
      customFromDate: this.customFromDate,
      customToDate: this.customToDate,
    });
    const filePrefix =
      this.reportMode === 'delivered' ? 'meal-delivered-report' : 'meal-ordered-report';
    XLSX.writeFile(workbook, `${filePrefix}-${suffix}.xlsx`);
  }

  trackByCenter(_: number, row: ReportRow): string {
    return row.locationId || row.locationName;
  }

  private applyWeekBounds(bounds: { fromDate: string; toDate: string }): void {
    this.customFromDate = bounds.fromDate;
    this.customToDate = bounds.toDate;
    this.weekLabel = formatReportWeekLabel(bounds.fromDate, bounds.toDate);
  }

  private applyReportMode(mode: MealGridReportMode): void {
    this.reportMode = mode;
    // Figma: title stays "Meal Report" for both Ordered and Delivered.
    this.reportTitle = 'Meal Report';
  }

  private formatCellValue(ordered: number, delivered: number): string {
    // Ordered: ordered count only, e.g. "5".
    // Delivered: delivered count only, e.g. "3".
    return formatReservedReceivedCell(
      ordered,
      delivered,
      this.reportMode === 'delivered' ? 'received' : 'reserved',
    );
  }

  private loadReport(options: { silent?: boolean } = {}): void {
    const range = resolveReportDateRange({
      timeframe: this.timeframe,
      selectedYear: '',
      selectedMonthNumber: '',
      customFromDate: this.customFromDate,
      customToDate: this.customToDate,
    });
    if (!range) {
      this.cells = [];
      this.rows = [];
      this.dateKeys = [];
      this.dateLabels = [];
      this.errorMessage = null;
      this.isInitialLoading = false;
      this.isRefreshing = false;
      return;
    }

    this.beginLoading(options);
    const programId = getProgramId() ?? this.seniorCenterProgramId;

    this.reportService
      .getReport({
        programId,
        fromDate: range.fromDate,
        toDate: range.toDate,
        locationId: this.filterCenterId || null,
      })
      .subscribe({
        next: (response) => {
          this.applyReportResponse(response, range.fromDate, range.toDate);
        },
        error: (err) => this.handleReportError(err),
      });
  }

  private beginLoading(options: { silent?: boolean }): void {
    if (!options.silent) {
      this.isInitialLoading = true;
    } else {
      this.isRefreshing = true;
    }
    this.errorMessage = null;
  }

  private applyReportResponse(
    response: MealOrderedReportResponse,
    fromDate: string,
    toDate: string,
  ): void {
    this.cells = response.cells ?? [];
    this.lastFromDate = fromDate;
    this.lastToDate = toDate;
    this.buildMatrix(fromDate, toDate);
    this.isInitialLoading = false;
    this.isRefreshing = false;
  }

  private handleReportError(err: { error?: { message?: string; Message?: string } }): void {
    this.cells = [];
    this.rows = [];
    this.dateKeys = [];
    this.dateLabels = [];
    this.isInitialLoading = false;
    this.isRefreshing = false;
    this.errorMessage =
      err?.error?.message ||
      err?.error?.Message ||
      'Unable to load meal ordered report. Please try again.';
  }

  private buildMatrix(fromDate: string, toDate: string): void {
    this.dateKeys = this.enumerateDates(fromDate, toDate);
    this.dateLabels = this.dateKeys.map((key) => this.formatDateLabel(key));
    this.rows = this.buildCenterRows((centerId, columnKey) => {
      const cell = this.findCell(centerId, columnKey);
      const ordered = cell?.ordered ?? 0;
      const delivered = cell?.delivered ?? 0;
      return this.formatCellValue(ordered, delivered);
    });
  }

  private buildCenterRows(
    valueFor: (centerId: string, columnKey: string) => string,
  ): ReportRow[] {
    let centersForRows: LocationItem[] = this.centers;
    if (this.filterCenterId) {
      centersForRows = this.centers.filter((c) => c.id === this.filterCenterId);
    }

    if (!this.filterCenterId) {
      const knownIds = new Set(centersForRows.map((c) => c.id));
      for (const cell of this.cells) {
        const id = cell.locationId ? String(cell.locationId) : '';
        if (id && !knownIds.has(id)) {
          centersForRows = [
            ...centersForRows,
            { id, locationName: cell.locationName, addressLine: '' },
          ];
          knownIds.add(id);
        }
      }
    }

    centersForRows = [...centersForRows].sort((a, b) =>
      a.locationName.localeCompare(b.locationName, undefined, { sensitivity: 'base' }),
    );

    return centersForRows.map((center) => {
      const values: Record<string, string> = {};
      for (const key of this.dateKeys) {
        values[key] = valueFor(center.id, key);
      }
      return {
        locationId: center.id,
        locationName: center.locationName || 'Unknown Center',
        values,
      };
    });
  }

  private findCell(centerId: string, dateKey: string): MealOrderedReportCell | undefined {
    return this.cells.find((cell) => {
      const locationKey = cell.locationId ? String(cell.locationId) : '';
      return locationKey === centerId && this.normalizeDateKey(cell.date) === dateKey;
    });
  }

  private enumerateDates(fromDate: string, toDate: string): string[] {
    const dates: string[] = [];
    const cursor = new Date(`${fromDate}T00:00:00`);
    const end = new Date(`${toDate}T00:00:00`);
    while (cursor <= end) {
      dates.push(this.toDateKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  }

  private toDateKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private normalizeDateKey(value: string): string {
    if (!value) {
      return '';
    }
    return value.slice(0, 10);
  }

  private formatDateLabel(dateKey: string): string {
    const date = new Date(`${dateKey}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return dateKey;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${month} ${day}`;
  }
}
